import { Button } from "@rio-cloud/rio-uikit";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import Card from "@rio-cloud/rio-uikit/Card";
import { ReactNode, useEffect, useState } from "react";
import { useT } from "@hooks";
import { normalizeText } from "@utils";

export const isDuplicatedName = (value: string, existingNames: string[]) =>
  !!value.trim() && existingNames.some((existing) => normalizeText(existing) === normalizeText(value));

export const CategoryTypeTooltip = () => {
  const t = useT();

  return (
    <div className="display-flex flex-column align-items-start gap-5 padding-5">
      <p className="margin-0">
        <span className="text-bold">{t("dialog.category.direct")}:</span> {t("dialog.category.directDesc")}
      </p>
      <p className="margin-0">
        <span className="text-bold">{t("dialog.category.operational")}:</span> {t("dialog.category.operationalDesc")}
      </p>
    </div>
  );
};

export const useCategoryDialogFields = (name?: string, type?: string, expenseType?: string, show?: boolean) => {
  const [error, setError] = useState("");
  const [errorCategorySelect, setErrorCategorySelect] = useState("");
  const [errorTypeSelect, setErrorTypeSelect] = useState("");

  const [valueCategory, setValueCategory] = useState(name || "");
  const [selectCategory, setSelectCategory] = useState("");
  const [selectType, setSelectType] = useState("");

  useEffect(() => {
    setValueCategory(name ?? "");
    setSelectType(type ?? "");
    setSelectCategory(expenseType ?? "");
  }, [name, type, expenseType, show]);

  const clearErrors = () => {
    setError("");
    setErrorTypeSelect("");
    setErrorCategorySelect("");
  };

  const clearValues = () => {
    setValueCategory("");
    setSelectCategory("");
    setSelectType("");
  };

  return {
    error,
    setError,
    errorCategorySelect,
    setErrorCategorySelect,
    errorTypeSelect,
    setErrorTypeSelect,
    valueCategory,
    setValueCategory,
    selectCategory,
    setSelectCategory,
    selectType,
    setSelectType,
    clearErrors,
    clearValues,
  };
};

interface SharedCreateDialogProps {
  show: boolean;
  title: string;
  placeholder: string;
  value: string;
  onValueChange: (value: string) => void;
  error: string;
  isDuplicateName: boolean;
  duplicateNameMessage: string;
  onClose: () => void;
  onSubmit: () => void;
  useOverflow: boolean;
  showDuplicateAlert?: boolean;
  duplicateAlertMessage?: string;
  fieldsBefore?: ReactNode;
  fieldsAfter?: ReactNode;
  maxLength?: number;
}

const SharedCreateDialog = ({
  show,
  title,
  placeholder,
  value,
  onValueChange,
  error,
  isDuplicateName,
  duplicateNameMessage,
  onClose,
  onSubmit,
  useOverflow,
  showDuplicateAlert,
  duplicateAlertMessage,
  fieldsBefore,
  fieldsAfter,
  maxLength,
}: SharedCreateDialogProps) => {
  const t = useT();

  return (
    <Dialog
      show={show}
      title={<span className="text-medium text-size-16">{title}</span>}
      body={
        <>
          {fieldsBefore}
          <div className={`form-group ${error || isDuplicateName ? "has-feedback has-error" : ""}`}>
            <label htmlFor="inputValue">
              <span>{placeholder}</span>
            </label>
            <div className="input-group">
              <input
                id="inputValue"
                type="text"
                className="form-control"
                value={value}
                onChange={(e) => onValueChange(e.target.value)}
                name="value"
                maxLength={maxLength}
              />
            </div>
            {error || isDuplicateName ? (
              <span className="form-control-feedback rioglyph rioglyph-error-sign"></span>
            ) : null}
            <span className="help-block">
              <span>
                {error || (isDuplicateName ? duplicateNameMessage || t("common.error.duplicateNameInline") : "")}
              </span>
            </span>
          </div>
          {fieldsAfter}
          {showDuplicateAlert && (
            <Card className="margin-top-10 bg-highlight-lightest">
              <div className="display-flex justify-content-start gap-10">
                <span
                  className="rioglyph rioglyph-light-bulb text-size-h2 text-color-map-marker-route margin-top-2"
                  aria-hidden="true"
                ></span>
                <div className="width-100pct">
                  <div className="text-size-18 text-medium">{t("common.label.important")}</div>
                  <div className="text-color-gray" style={{ whiteSpace: "pre-line" }}>
                    {duplicateAlertMessage}
                  </div>
                </div>
              </div>
            </Card>
          )}
        </>
      }
      onClose={onClose}
      footer={
        <div className="display-flex align-items-center justify-content-end gap-10 flex-wrap">
          <Button onClick={onClose} className="width-150 rounded-small width-100pct-xs">
            {t("common.action.cancel")}
          </Button>
          <Button
            onClick={onSubmit}
            bsStyle={Button.PRIMARY}
            className="width-150 rounded-small width-100pct-xs"
            disabled={isDuplicateName}
          >
            {t("common.action.save")}
          </Button>
        </div>
      }
      showCloseButton
      useOverflow={useOverflow}
    />
  );
};

export default SharedCreateDialog;
